import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, Spinner } from "../../../shared";
import { renderAmount } from "../../../utils";

const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const CurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white p-2 border border-gray-200 rounded shadow text-xs">
      <p className="font-bold">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}:{" "}
          {String(entry.dataKey).toLowerCase().includes("income") ||
          String(entry.dataKey).toLowerCase().includes("payout") ||
          String(entry.dataKey).toLowerCase().includes("revenue") ||
          String(entry.dataKey).toLowerCase().includes("profit")
            ? renderAmount(entry.value)
            : entry.value}
        </p>
      ))}
    </div>
  );
};

const EmptyChart = () => (
  <div className="h-[280px] flex items-center justify-center text-sm text-gray-500">
    No data to display for this time range.
  </div>
);

const renderCartesianChart = ({ chartType, data, series }) => {
  const Chart =
    chartType === "bar" ? BarChart : chartType === "line" ? LineChart : AreaChart;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <Chart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" />
        <YAxis />
        <Tooltip content={<CurrencyTooltip />} />
        <Legend />
        {series.map((item, index) => {
          const color = COLORS[index % COLORS.length];
          if (chartType === "bar") {
            return <Bar key={item.key} dataKey={item.key} fill={color} />;
          }
          if (chartType === "line") {
            return (
              <Line
                key={item.key}
                type="monotone"
                dataKey={item.key}
                stroke={color}
                strokeWidth={2}
              />
            );
          }
          return (
            <Area
              key={item.key}
              type="monotone"
              dataKey={item.key}
              stroke={color}
              fill={color}
              fillOpacity={0.2}
            />
          );
        })}
      </Chart>
    </ResponsiveContainer>
  );
};

const renderPieChart = ({ data, series }) => {
  const dataKey = series?.[0]?.key || "count";

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius={65}
          outerRadius={95}
          label
        >
          {data.map((entry, index) => (
            <Cell key={entry.label} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

const ChartWidget = ({ widget, loading }) => {
  const chartData = widget?.data || {};
  const data = Array.isArray(chartData.data) ? chartData.data : [];
  const series = Array.isArray(chartData.series) ? chartData.series : [];
  const chartType = widget?.chartType || chartData.chartType || "area";

  return (
    <Card className="h-full min-h-[380px] rounded-lg">
      <div className="mb-3 text-xs md:text-sm uppercase text-gray-500 font-bold">
        {widget?.title}
      </div>
      {loading ? (
        <div className="h-[280px] flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : !data.length ? (
        <EmptyChart />
      ) : chartType === "pie" || chartType === "donut" ? (
        renderPieChart({ data, series })
      ) : (
        renderCartesianChart({ chartType, data, series })
      )}
    </Card>
  );
};

export default ChartWidget;
